// Inventario de equipos (Activos). Tabla + KPIs + búsqueda + alta manual.
// Datos: /admin/assets/assets (mcp_django). Tokens de tema (claro/oscuro).

import { useEffect, useMemo, useState } from 'react';

import { createAsset, listAssets, recordHourMeter } from '../api';
import type { Asset, AssetStatus } from '../types';
import { ASSET_STATUS_LABEL, RATE_UNIT_LABEL } from '../types';

const STATUS_FILTERS: Array<{ value: '' | AssetStatus; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'available', label: 'Disponibles' },
  { value: 'rented', label: 'Rentados' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'retired', label: 'Baja' },
];

function StatusBadge({ status }: { status: AssetStatus }) {
  const cls: Record<AssetStatus, string> = {
    available: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    rented: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
    maintenance: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    retired: 'bg-muted text-muted-foreground',
    lost: 'bg-red-500/15 text-red-600 dark:text-red-400',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}>
      {ASSET_STATUS_LABEL[status]}
    </span>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className={`text-2xl font-semibold ${tone ?? 'text-foreground'}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

export function InventoryPage() {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | AssetStatus>('');
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setAssets(await listAssets({ q, status, limit: 1000 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando inventario');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce búsqueda/filtro
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status]);

  const kpis = useMemo(() => {
    const a = assets || [];
    return {
      total: a.length,
      available: a.filter((x) => x.status === 'available').length,
      rented: a.filter((x) => x.status === 'rented').length,
      maintenance: a.filter((x) => x.status === 'maintenance').length,
    };
  }, [assets]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Equipos totales" value={kpis.total} />
        <Kpi label="Disponibles" value={kpis.available} tone="text-emerald-600 dark:text-emerald-400" />
        <Kpi label="Rentados" value={kpis.rented} tone="text-blue-600 dark:text-blue-400" />
        <Kpi label="En mantenimiento" value={kpis.maintenance} tone="text-amber-600 dark:text-amber-400" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por modelo, nº interno, serie o familia…"
          className="min-w-[240px] flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as '' | AssetStatus)}
          className="rounded-md border border-border bg-background px-2 py-2 text-sm text-foreground"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          + Alta de equipo
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">Equipo</th>
              <th className="px-3 py-2 font-medium">Nº interno</th>
              <th className="px-3 py-2 font-medium">Serie</th>
              <th className="px-3 py-2 font-medium">Estado</th>
              <th className="px-3 py-2 font-medium">Cliente / Obra</th>
              <th className="px-3 py-2 font-medium text-right">Tarifa</th>
              <th className="px-3 py-2 font-medium text-right">Horómetro</th>
            </tr>
          </thead>
          <tbody>
            {loading && !assets ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Cargando inventario…</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-red-500">{error}</td></tr>
            ) : !assets || assets.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Sin equipos. Da de alta el primero con "+ Alta de equipo".</td></tr>
            ) : (
              assets.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{a.category || a.name}</div>
                    {a.category && a.name ? <div className="text-xs text-muted-foreground">{a.name}</div> : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-foreground">{a.identifier || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                    {String((a.attributes as any)?.num_serie || '—')}
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={a.status} /></td>
                  <td className="px-3 py-2 text-foreground">
                    {a.status === 'rented' ? (
                      <>
                        <div>{a.current_holder_name || '—'}</div>
                        {(a.attributes as any)?.obra_nombre ? (
                          <div className="text-xs text-muted-foreground">{String((a.attributes as any).obra_nombre)}</div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-foreground">
                    {a.default_rate_amount != null
                      ? `$${a.default_rate_amount}${a.default_rate_unit ? ` / ${RATE_UNIT_LABEL[a.default_rate_unit]}` : ''}`
                      : '—'}
                  </td>
                                  <HourMeterCell asset={a} onSaved={load} />
                  </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {assets ? (
        <div className="text-xs text-muted-foreground">{assets.length} equipos</div>
      ) : null}

      {showCreate ? (
        <CreateAssetModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      ) : null}
    </div>
  );
}

function CreateAssetModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    category: '', name: '', identifier: '', num_serie: '',
    default_rate_amount: '', default_rate_unit: 'day', location: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.name.trim() && !form.category.trim()) {
      setErr('Pon al menos familia o modelo del equipo.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await createAsset({
        name: form.name.trim() || form.category.trim(),
        category: form.category.trim(),
        identifier: form.identifier.trim(),
        attributes: form.num_serie.trim() ? { num_serie: form.num_serie.trim() } : {},
        location: form.location.trim(),
        default_rate_amount: form.default_rate_amount ? Number(form.default_rate_amount) : null,
        default_rate_unit: form.default_rate_unit as any,
        status: 'available',
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo crear el equipo');
    } finally {
      setSaving(false);
    }
  }

  const field = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-lg font-semibold text-foreground">Alta de equipo</h3>
        <div className="space-y-3">
          <input className={field} placeholder="Familia / línea (ej. Allanadora)" value={form.category} onChange={(e) => set('category', e.target.value)} />
          <input className={field} placeholder="Modelo (ej. BT65)" value={form.name} onChange={(e) => set('name', e.target.value)} />
          <input className={field} placeholder="Nº interno / placa" value={form.identifier} onChange={(e) => set('identifier', e.target.value)} />
          <input className={field} placeholder="Nº de serie" value={form.num_serie} onChange={(e) => set('num_serie', e.target.value)} />
          <div className="flex gap-2">
            <input className={field} type="number" placeholder="Tarifa $" value={form.default_rate_amount} onChange={(e) => set('default_rate_amount', e.target.value)} />
            <select className={field} value={form.default_rate_unit} onChange={(e) => set('default_rate_unit', e.target.value)}>
              <option value="day">por día</option>
              <option value="week">por semana</option>
              <option value="month">por mes</option>
              <option value="flat">fijo</option>
            </select>
          </div>
          <input className={field} placeholder="Ubicación (opcional)" value={form.location} onChange={(e) => set('location', e.target.value)} />
          {err ? <div className="text-sm text-red-500">{err}</div> : null}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted/50">Cancelar</button>
          <button onClick={submit} disabled={saving} className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Captura del horómetro por equipo (RentalOS F1/F3).
 *
 * Las 74 unidades están en 0 h: el ciclo de servicio automático nunca dispara
 * y todas dicen "faltan 200 h" aunque lleven años trabajando. La lectura se
 * toma en el patio (típicamente al regreso de obra), así que se captura aquí
 * mismo en la fila —sin abrir modal— para que no cueste trabajo hacerlo.
 */
function HourMeterCell({ asset, onSaved }: { asset: Asset; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const h = Number(hours);
    if (!hours || Number.isNaN(h) || h < 0) { setErr('Lectura inválida'); return; }
    // El horómetro no retrocede: una lectura menor casi siempre es un typo.
    if (asset.hour_meter != null && h < Number(asset.hour_meter)) {
      setErr(`Menor a la actual (${asset.hour_meter} h)`);
      return;
    }
    setSaving(true); setErr(null);
    try {
      await recordHourMeter(asset.id, { hours: h });
      setEditing(false); setHours('');
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally { setSaving(false); }
  }

  const current = (asset as unknown as { hour_meter?: number }).hour_meter;

  if (!editing) {
    return (
      <td className="px-3 py-2 text-right">
        <button
          onClick={() => { setEditing(true); setHours(current ? String(current) : ''); }}
          className="text-sm text-primary hover:underline"
          title="Registrar lectura del horómetro"
        >
          {current ? `${current} h` : 'Capturar'}
        </button>
      </td>
    );
  }

  return (
    <td className="px-3 py-2 text-right">
      <div className="flex items-center justify-end gap-1">
        <input
          type="number" min={0} step="0.1" autoFocus
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          className="w-24 rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
          placeholder="horas"
        />
        <button onClick={save} disabled={saving} className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">
          {saving ? '…' : 'OK'}
        </button>
        <button onClick={() => { setEditing(false); setErr(null); }} className="px-1 text-xs text-muted-foreground">✕</button>
      </div>
      {err ? <div className="mt-1 text-xs text-destructive">{err}</div> : null}
    </td>
  );
}
